import { postJson } from "@lib/http";

export type TutorSpeechResponse =
  | { kind: "url"; url: string }
  | { kind: "buffer"; buffer: ArrayBuffer };

/** Dictation audio must always declare the learning language (targetLocale), not UI/native locale. */
export async function fetchDictationSpeech(
  text: string,
  targetLocale: string,
  init?: Pick<RequestInit, "signal">,
): Promise<TutorSpeechResponse | null> {
  const locale = targetLocale.trim();
  if (!locale) {
    return null;
  }

  return fetchTutorSpeech(text, { speedKind: "dictation", targetLocale: locale }, init);
}

export async function fetchTutorSpeech(
  text: string,
  body: Record<string, unknown> = {},
  init?: Pick<RequestInit, "signal">,
): Promise<TutorSpeechResponse | null> {
  const res = await postJson("/api/voice/speak", { text, ...body }, { signal: init?.signal });

  if (!res.ok) return null;

  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const payload = (await res.json()) as { url?: string };
    if (typeof payload.url === "string" && payload.url.length > 0) {
      return { kind: "url", url: payload.url };
    }
    return null;
  }

  return { kind: "buffer", buffer: await res.arrayBuffer() };
}

export async function fetchTutorSpeechBuffer(
  text: string,
  body: Record<string, unknown> = {},
  init?: Pick<RequestInit, "signal">,
): Promise<ArrayBuffer | null> {
  const speech = await fetchTutorSpeech(text, body, init);
  if (!speech) return null;
  if (speech.kind === "buffer") return speech.buffer;

  const audioRes = await fetch(speech.url, { signal: init?.signal });
  if (!audioRes.ok) return null;
  return audioRes.arrayBuffer();
}
